import { makeAutoObservable, runInAction } from 'mobx'
import { ApiError, NetworkError } from '@/shared/api/http'

/**
 * Загружаемые данные: значение, признак загрузки, ошибка.
 *
 * Одна такая тройка нужна почти каждому экрану, и написанная руками
 * двадцать раз она двадцатью способами и ошибается: где-то забыли
 * снять loading при отказе, где-то стёрли данные при обновлении
 * и экран мигнул пустотой.
 *
 * Отдельное правило здесь одно и важное: **при отказе прежние данные
 * НЕ стираются**. Список кошельков, который исчез из-за моргнувшей
 * сети, читается как «кошельки удалились», а адрес перевода, пропавший
 * с экрана в момент оплаты, — это потерянный платёж.
 */
export class Resource<T> {
  value: T | null = null
  loading = false
  error: Error | null = null
  /** Была ли хоть одна успешная загрузка. Отличает «пусто» от «ещё не грузили». */
  loaded = false

  private version = 0
  private readonly fetcher: () => Promise<T>

  constructor(fetcher: () => Promise<T>) {
    this.fetcher = fetcher
    makeAutoObservable<this, 'fetcher' | 'version'>(this, {
      fetcher: false,
      version: false,
    })
  }

  async load(): Promise<void> {
    // Номер запроса: пока идёт медленный, мог начаться быстрый.
    // Без этой проверки поздний ответ старого запроса затирает
    // свежие данные — на фильтрах истории это выглядит как «список
    // сам возвращается к прошлому фильтру».
    const ticket = ++this.version
    this.loading = true
    this.error = null
    try {
      const value = await this.fetcher()
      if (ticket !== this.version) return
      runInAction(() => {
        this.value = value
        this.loaded = true
        this.loading = false
      })
    } catch (cause) {
      if (ticket !== this.version) return
      if (cause instanceof DOMException && cause.name === 'AbortError') return
      runInAction(() => {
        this.error = toError(cause)
        this.loading = false
      })
    }
  }

  /** Подставить значение, полученное иначе (например ответом на действие). */
  set(value: T): void {
    this.version += 1
    this.value = value
    this.loaded = true
    this.loading = false
    this.error = null
  }

  reset(): void {
    this.version += 1
    this.value = null
    this.loading = false
    this.error = null
    this.loaded = false
  }

  /** Первая загрузка: показывать скелет, а не пустой экран. */
  get initialLoading(): boolean {
    return this.loading && !this.loaded
  }
}

export function toError(cause: unknown): Error {
  if (cause instanceof ApiError || cause instanceof NetworkError) return cause
  if (cause instanceof Error) return cause
  return new NetworkError(cause)
}
